import { BrowserRouter as Router, Routes, Route, Outlet, Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import UserRegistration from './pages/auth/UserRegistration';
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { AuthProvider, useAuth } from './components/auth/AuthContext';
import { BrandIdentityProvider, useBrandIdentity } from './contexts/BrandIdentityContext';
import { AccountProvider } from './contexts/AccountContext';
import RequireSubscription from './components/auth/RequireSubscription';

// Home - Rota pública
import Home from './pages/Home';

// 🆕 Páginas Legais - Rotas públicas
import PlansPage from './pages/legal/PlansPage';
import TermsPage from './pages/legal/TermsPage';
import PrivacyPage from './pages/legal/PrivacyPage';
import AboutPage from './pages/legal/AboutPage';

import CheckoutSuccess from './pages/checkout/CheckoutSuccess';
import CheckoutCancel from './pages/checkout/CheckoutCancel';

// 🆕 Onboarding
import OnboardingWizard from './pages/onboarding/OnboardingWizard';

// Content Generation - Rotas
import ContentBatchesList from './pages/contents/ContentBatchesList';
import ContentWizard from './pages/contents/manual/ContentWizard';
import ContentBatchViewer from './pages/contents/ContentBatchViewer';
import PlacidContentEditor from './pages/contents/PlacidContentEditor';
import CreatomateContentEditor from './pages/contents/CreatomateContentEditor';
import TemplatedContentEditor from './pages/contents/TemplatedContentEditor';
import ReactRenderContentEditor from './pages/contents/ReactRenderContentEditor';
import GptImageChatEditor from './pages/contents/GptImageChatEditor';

// User Account
import UserAccount from './pages/user/UserAccount';

// 🆕 Novos componentes para fluxo Instagram
import ContentCreationSelector from './pages/contents/ContentCreationSelector';
import InstagramWizard from './pages/contents/instagram/InstagramWizard';

// Social
import SocialConnectionsPage from './pages/social/SocialConnectionsPage';
import ScheduledPostsPage from './pages/social/ScheduledPostsPage';

// 🆕 Admin
import AdminConfigPage from './pages/admin/AdminConfigPage';
import LabPage from './pages/admin/LabPage';
import LabReviewPage from './pages/admin/LabReviewPage';
import AdminServicesPage from './pages/admin/AdminServicesPage';
import AdminAddonsPage from './pages/admin/AdminAddonsPage';

// 🆕 Servicos
import ServicesPage from './pages/services/ServicesPage';
import ServiceRequestForm from './pages/services/ServiceRequestForm';
import TicketDetailPage from './pages/services/TicketDetailPage';
import ServicesLandingPage from './pages/services/ServicesLandingPage';

// 🆕 Layout com Sidebar
import Sidebar from './components/layout/Sidebar';

// Widget de Chat AI
import AIChatWidget from './components/widgets/AIChatWidget';

// 🆕 Stripe Service para checkout
import { stripeService } from './services/StripeService';

import './App.css';

// ✨ Configuração - hello, social (valores padrão)
const getDefaultConfig = (brandColors) => ({
  companyName: "hello, social",
  logoUrl: "/mnt/user-data/uploads/logo_2.png",
  colors: {
    primary: brandColors?.primary || '#FFB800',
    primaryDark: brandColors?.primaryDark || '#FF9500',
    secondary: brandColors?.secondary || '#000000',
    accent: brandColors?.accent || '#FFFFFF',
    black: '#000000',
    gray: {
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#E5E5E5',
      300: '#D4D4D4',
      400: '#A3A3A3',
      500: '#737373',
      600: '#525252',
      700: '#404040',
      800: '#262626',
      900: '#171717'
    }
  }
});

// Config padrão (será sobrescrito pelo contexto)
let config = getDefaultConfig();

// 🆕 Componente inteligente para rota /checkout
// Processa o query param e redireciona para Stripe ou /plans
const CheckoutRouter = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState(null);

  const plan = searchParams.get('plan');

  useEffect(() => {
    const processCheckout = async () => {
      // Se não tem plano especificado, redirecionar para /plans
      if (!plan) {
        navigate('/plans', { replace: true });
        return;
      }

      // Validar plano
      const validPlans = ['basic', 'pro', 'max'];
      if (!validPlans.includes(plan)) {
        navigate('/plans', { replace: true });
        return;
      }

      // Se não está logado, redirecionar para registro com plano
      if (!currentUser) {
        navigate(`/register?plan=${plan}`, { replace: true });
        return;
      }

      // Usuário logado - redirecionar para Stripe Checkout
      try {
        await stripeService.redirectToCheckout(plan);
      } catch (err) {
        console.error('Erro ao iniciar checkout:', err);
        setError('Erro ao processar checkout. Redirecionando...');
        setTimeout(() => {
          navigate('/plans', { replace: true });
        }, 2000);
      }
    };

    processCheckout();
  }, [plan, currentUser, navigate]);

  // Loading state
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center">
      <div className="text-center">
        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8 max-w-md">
            <p className="text-red-600 mb-4">{error}</p>
            <div className="w-8 h-8 mx-auto border-4 border-red-200 border-t-red-500 rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-amber-100 to-orange-100 rounded-2xl flex items-center justify-center">
              <svg className="w-8 h-8 text-amber-500 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Redirecionando para o checkout...
            </h2>
            <p className="text-gray-500">
              Aguarde enquanto preparamos seu pagamento seguro.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

// 🆕 Layout Wrapper para rotas protegidas com Sidebar
const ProtectedLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <Sidebar 
        isOpen={sidebarOpen} 
        setIsOpen={setSidebarOpen}
        isCollapsed={sidebarCollapsed}
        setIsCollapsed={setSidebarCollapsed}
      />

      {/* Main Content */}
      <main
        className={`min-h-screen transition-all duration-300 ease-out ${
          sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'
        }`}
      >
        <Outlet />
      </main>
    </div>
  );
};

// Componente wrapper para prover brand identity context
const AppContent = () => {
  const { colors: brandColors } = useBrandIdentity();
  const { currentUser } = useAuth();
  // Atualizar config com cores do usuário
  config = getDefaultConfig(brandColors);

  return (
    <>
    {currentUser && <AIChatWidget />}
    <Routes>
      {/* ========================================
          ROTAS PÚBLICAS (sem Sidebar)
          ======================================== */}
      <Route path="/" element={<Home />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<UserRegistration />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      
      {/* 🆕 Rotas Legais - Públicas */}
      <Route path="/plans" element={<PlansPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/services-landing" element={<ServicesLandingPage />} />

      {/* 🆕 Checkout Router Inteligente - Processa e redireciona */}
      <Route path="/checkout" element={<CheckoutRouter />} />
      <Route path="/checkout/success" element={<CheckoutSuccess />} />
      <Route path="/checkout/cancel" element={<CheckoutCancel />} />

      {/* ========================================
          ROTAS PROTEGIDAS (sem Sidebar - tela cheia)
          ======================================== */}
      <Route element={<ProtectedRoute />}>
        {/* 🆕 Onboarding - Tela cheia sem sidebar */}
        <Route path="/onboarding" element={<OnboardingWizard />} />
        {/* Lab Review - Tela cheia sem sidebar (Tinder-style) */}
        <Route path="/admin/lab/review" element={<LabReviewPage />} />
        {/* User Account */}
        <Route element={<ProtectedLayout />}>
          <Route path="/account" element={<UserAccount />} />

          {/* 🆕 Servicos - Acessivel para todos usuarios logados (inclusive free) */}
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/request" element={<ServiceRequestForm />} />
          <Route path="/services/tickets/:ticketId" element={<TicketDetailPage />} />
        </Route>

      </Route>

      {/* ========================================
          ROTAS PROTEGIDAS (com Sidebar)
          ======================================== */}
          
      <Route element={<RequireSubscription />}>
      <Route element={<ProtectedRoute />}>
        <Route element={<ProtectedLayout />}>
          
          {/* ========================================
              CONTENT GENERATION ROUTES
              ======================================== */}
          
          {/* Lista de batches/projetos */}
          <Route path="/contents" element={<ContentBatchesList />} />
          
          {/* 🆕 Seletor de tipo de criação (Instagram ou Manual) */}
          <Route path="/contents/new" element={<ContentCreationSelector />} />
          
          {/* 🆕 Fluxo Instagram (apenas digitar @) */}
          <Route path="/contents/new/instagram" element={<InstagramWizard />} />
          
          {/* Fluxo Manual (wizard completo existente) */}
          <Route path="/contents/new/manual" element={<ContentWizard />} />


          {/* Visualizador de batch */}
          <Route path="/contents/batch/:batchId" element={<ContentBatchViewer />} />
          
          {/* ✨ Editor Placid para conteúdos editáveis */}
          <Route path="/contents/batch/:batchId/edit/:contentId" element={<PlacidContentEditor />} />
          <Route path="/contents/batch/:batchId/edit-creatomate/:contentId" element={<CreatomateContentEditor />} />
          <Route path="/contents/batch/:batchId/edit-templated/:contentId" element={<TemplatedContentEditor />} />
          <Route path="/contents/batch/:batchId/edit-react-render/:contentId" element={<ReactRenderContentEditor />} />
          <Route path="/contents/batch/:batchId/edit-gpt-image/:contentId" element={<GptImageChatEditor />} />
          
          {/* ========================================
              USER & SOCIAL ROUTES
              ======================================== */}
          
          

          {/* Social Connections */}
          <Route path="/social" element={<SocialConnectionsPage />} />
          <Route path="/calendar" element={<ScheduledPostsPage />} />

          {/* ========================================
              🆕 ADMIN ROUTES (apenas is_admin = true)
              ======================================== */}
          <Route path="/admin/config" element={<AdminConfigPage />} />
          <Route path="/admin/lab" element={<LabPage />} />
          <Route path="/admin/services" element={<AdminServicesPage />} />
          <Route path="/admin/addons" element={<AdminAddonsPage />} />

          {/* ========================================
              PLACEHOLDER ROUTES (Em breve)
              ======================================== */}
          
          {/* Sobre - Em breve */}
          <Route path="/about" element={<AboutPage/>} />
          
        </Route>
      </Route>
      </Route>
    </Routes>
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <AccountProvider>
        <BrandIdentityProvider>
          <Router>
            <AppContent />
          </Router>
        </BrandIdentityProvider>
      </AccountProvider>
    </AuthProvider>
  );
}

export default App;

export { config };